// require-opd
export default defineNuxtRouteMiddleware(async (to, from) => {
   const { getAuthed, user } = useAuthStore();
   const { onboardingCourses, queryOnboardingCourses } = useOnboardingCourses();
   const pdDeadlineRoute = "/pd-deadline";
   // Session cookie rather than sessionStorage so the server makes the same redirect decision.
   const dismissedReminderCookie = useCookie<boolean>("dismissedDeadlineReminder");

   await nextTick();

   // Handle pd-deadline route for unauthenticated users
   if (to.path === pdDeadlineRoute && !getAuthed) {
      return navigateTo({ path: "/" });
   }

   // If no user data, do nothing
   if (!user || !getAuthed) return;

   // Early return if PD course is completed
   if (user?.is_pd_course_completed) {
      if (to.path === pdDeadlineRoute) {
         return navigateTo(`/home/${user.school.slug}`);
      }
      return;
   }

   // Ensure onboardingCourses are defined before proceeding
   if (!onboardingCourses?.value) {
      try {
         await queryOnboardingCourses();
         await nextTick();

         // Re-check auth status after API call
         if (!getAuthed || !user) {
            return navigateTo({ path: "/" });
         }
      } catch (error) {
         // API call failed - handle error and always redirect
         return navigateTo({ path: "/" });
      }
   }

   // Final auth check before PD logic
   if (!getAuthed || !user) {
      return navigateTo({ path: "/" });
   }

   const allowedPaths = [pdDeadlineRoute, ...(onboardingCourses.value ?? [])];

   // If authenticated and PD course not completed, enforce deadline reminders
   if (
      getAuthed &&
      user?.is_pd_course_completed === false &&
      user?.pd_course_deadline &&
      !allowedPaths.some((path) => to.path.includes(path))
   ) {
      const pdCourseDeadline = new Date(user.pd_course_deadline);
      const today = new Date();
      const oneWeekFromToday = new Date();
      oneWeekFromToday.setDate(today.getDate() + 7);

      const deadlineWithinAWeek =
         pdCourseDeadline >= today && pdCourseDeadline <= oneWeekFromToday;
      const deadlineHasPassed = pdCourseDeadline < today;
      const extendedDeadline = user.pd_deadline_extended;
      const dismissedReminder = dismissedReminderCookie.value ?? false;

      let pdDeadlineStatus = "";

      if (deadlineWithinAWeek && !extendedDeadline) {
         pdDeadlineStatus = "reminder";
      } else if (deadlineHasPassed && !extendedDeadline) {
         pdDeadlineStatus = "extend";
      } else if (
         deadlineWithinAWeek &&
         extendedDeadline &&
         !dismissedReminder
      ) {
         pdDeadlineStatus = "extendedReminder";
      } else if (deadlineHasPassed && extendedDeadline) {
         pdDeadlineStatus = "frozen";
      }

      if (pdDeadlineStatus) {
         return navigateTo({
            name: "pd-deadline-modal",
            query: { status: pdDeadlineStatus },
         });
      }
   }
});
